import { call, put, takeEvery } from "redux-saga/effects";
import type { PayloadAction } from "@reduxjs/toolkit";
import { message } from "antd";
import request from "../../../utils/request";
import { API_ENDPOINT } from "../../../utils/constants";
import { handleAxiosError } from "../../../utils/APIValidations";
import {
  loadUsers,
  usersLoaded,
  loadUsersError,
  authorizeUser,
  userAuthorized,
  authorizeUserError,
  deleteUser,
  userDeleted,
  deleteUserError,
  createUser,
  userCreated,
  createUserError,
} from "./slice";
import type {
  AdminUser,
  AdminUserListResponse,
  AuthorizeUserPayload,
  CreateUserPayload,
  DeleteUserPayload,
  LoadUsersPayload,
} from "./types";

export function* fetchUsers({
  payload,
}: PayloadAction<LoadUsersPayload | undefined>) {
  try {
    const params = new URLSearchParams();
    if (payload?.startDate) params.set("start_date", payload.startDate);
    if (payload?.endDate) params.set("end_date", payload.endDate);

    const query = params.toString();
    const response: AdminUserListResponse = yield call(
      request,
      `${API_ENDPOINT}get-all-users${query ? `?${query}` : ""}`,
    );
    yield put(usersLoaded(response));
  } catch (e) {
    yield call(handleAxiosError, e);
    yield put(loadUsersError());
  }
}

export function* putAuthorizeUser({
  payload,
}: PayloadAction<AuthorizeUserPayload>) {
  try {
    const response: AdminUser = yield call(
      request,
      `${API_ENDPOINT}admin-approve-user?user_id=${payload.userId}`,
      { method: "PUT" },
    );
    yield put(userAuthorized(response));
    message.success("User authorized successfully");
  } catch (e) {
    yield call(handleAxiosError, e);
    yield put(authorizeUserError());
  }
}

export function* deleteAdminUser({
  payload,
}: PayloadAction<DeleteUserPayload>) {
  try {
    yield call(
      request,
      `${API_ENDPOINT}admin-delete-user?user_id=${payload.userId}`,
      { method: "DELETE" },
    );
    yield put(userDeleted({ userId: payload.userId }));
    message.success("User deleted successfully");
  } catch (e) {
    yield call(handleAxiosError, e);
    yield put(deleteUserError());
  }
}

export function* postCreateUser({
  payload,
}: PayloadAction<CreateUserPayload>) {
  try {
    const response: AdminUser = yield call(
      request,
      `${API_ENDPOINT}admin-add-user`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    yield put(userCreated(response));
  } catch (e) {
    yield call(handleAxiosError, e);
    yield put(createUserError());
  }
}

export default function* saga() {
  yield takeEvery(loadUsers.type, fetchUsers);
  yield takeEvery(authorizeUser.type, putAuthorizeUser);
  yield takeEvery(deleteUser.type, deleteAdminUser);
  yield takeEvery(createUser.type, postCreateUser);
}
